/**
 * CreateParticipantForm — 8-sektioners inline-formulär för ny deltagare.
 *
 * Sektioner:
 *   1. Grunduppgifter
 *   2. Verksamhetskoppling (upprepningsbara rader)
 *   3. Status
 *   4. Anhöriga/stödpersoner (upprepningsbara rader)
 *   5. Samtycken
 *   6. Viktig information (skyddat fält)
 *   7. Dokument
 *   (8. Footer / spara-knapp)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { X, Plus, Trash2, ShieldAlert, Upload } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Konstanter ────────────────────────────────────────────────────────────────

const STATUSES = ['Aktiv', 'Inväntar_beslut', 'Pausad', 'Avslutad'];

const CLOSURE_REASONS = [
  'Fullföljd process',
  'Övergång till arbete',
  'Övergång till studier',
  'Övergång till annan insats',
  'Avbrott',
  'Ej påbörjad',
  'Administrativt avslut',
  'Annan orsak',
];

const PAUSE_REASONS = [
  'Inväntar deltagarens återkomst',
  'Hälsoskäl eller återhämtning',
  'Praktiska hinder',
  'Inväntar ny planering',
  'Annan orsak',
];

const ACTIVITIES = [
  'Kartläggning', 'Coachning', 'Matchning', 'Arbetsplatspraktik',
  'CV-skrivning', 'Intervjuträning', 'Arbetsträning', 'Aktivering',
  'Motivationsarbete', 'Kompetenshöjning', 'Språkutveckling', 'Samhällsorientering',
];

const OUTCOMES = [
  'Arbete (heltid)', 'Arbete (deltid)', 'Studier',
  'Ny insats', 'Avbrott', 'Administrativt avslut',
];

const DOC_TYPES = [
  { value: 'gdpr_samtycke',             label: 'GDPR-samtycke' },
  { value: 'info_delning',              label: 'Samtycke informationsdelning' },
  { value: 'kontaktperson_viktig_info', label: 'Kontaktperson och viktig info' },
  { value: 'beslut',                    label: 'Beslut' },
];

const ALLOWED_FILETYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// ── PIN-validering ─────────────────────────────────────────────────────────────

const PIN_RE = /^(\d{8}-?\d{4}|\d{6}-?\d{4})$/;

function validatePin(pin) {
  return PIN_RE.test(pin.trim());
}

// ── Tomma rad-initialer ────────────────────────────────────────────────────────

const emptyEnrollment = () => ({
  area: '', category: '', intervention: '', intervention_type: '',
  activities: [], outcomes: [],
  start_date: '', planned_end_date: '', actual_end_date: '',
  case_number: '',
});

const emptyContact = () => ({ name: '', relationship: '', phone: '' });

// ── Cascading catalog helpers ──────────────────────────────────────────────────

const getAreas = (catalog) => catalog.map((c) => c.omrade);

const getCategories = (catalog, area) => {
  const entry = catalog.find((c) => c.omrade === area);
  return entry ? entry.kategorier.map((k) => k.kategori) : [];
};

const getInterventions = (catalog, area, category) => {
  const entry = catalog.find((c) => c.omrade === area);
  if (!entry) return [];
  const kat = entry.kategorier.find((k) => k.kategori === category);
  return kat ? kat.insatser : [];
};

const resolveInsatstyp = (catalog, area, category, intervention) => {
  const insatser = getInterventions(catalog, area, category);
  const match = insatser.find((i) => i.insats === intervention);
  return match ? match.insatstyp : '';
};

// ── Stil-hjälp ─────────────────────────────────────────────────────────────────

const selectCls = (err) =>
  `w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

const sectionCls = 'border rounded-xl p-6 space-y-4';

// ── Sektionsrubrik ─────────────────────────────────────────────────────────────

const SectionHeader = ({ num, title, sub }) => (
  <div className="mb-2">
    <div className="flex items-center gap-2">
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-secondary"
        style={{ background: '#F9CF3F' }}
      >
        {num}
      </span>
      <h3 className="font-heading font-bold text-lg">{title}</h3>
    </div>
    {sub && <p className="text-sm text-muted-foreground mt-1 ml-9">{sub}</p>}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// HUVUDKOMPONENT
// ══════════════════════════════════════════════════════════════════════════════

const CreateParticipantForm = ({ onSave, onCancel }) => {
  const { user } = useAuth();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [catalog, setCatalog]   = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [submitting, setSub]    = useState(false);
  const [errors, setErrors]     = useState({});

  // ── Formulärdata ──────────────────────────────────────────────────────────
  const [s1, setS1] = useState({
    first_name: '', last_name: '',
    personal_identity_number: '',
    phone: '', email: '',
    entity_name: '',
    assigned_user_id: '',
  });

  const [enrollments, setEnrollments] = useState([]);

  const [s3, setS3] = useState({
    status: 'Aktiv',
    status_comment: '',
    closure_reason: '',
    closure_date: '',
    pause_reason: '',
  });

  const [contacts, setContacts] = useState([emptyContact(), emptyContact()]);

  const [s5, setS5] = useState({
    gdpr_processing: false,
    info_sharing: false,
    contact_support_person: false,
  });

  const [s6, setS6] = useState({ important_information: '' });

  const [documents, setDocuments] = useState([]);

  // ── Ladda katalog + org-användare ─────────────────────────────────────────
  useEffect(() => {
    api.get('/catalog/verksamheter').then((r) => setCatalog(r.data || [])).catch(() => {});
    api.get('/users').then((r) => setOrgUsers(r.data || [])).catch(() => {});
  }, []);

  // ── Sek 1 helpers ─────────────────────────────────────────────────────────
  const setS1Field = (field, value) => {
    setS1((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  // ── Sek 2: enrollment-rader ────────────────────────────────────────────────
  const addEnrollment = () => setEnrollments((p) => [...p, emptyEnrollment()]);

  const removeEnrollment = (idx) =>
    setEnrollments((p) => p.filter((_, i) => i !== idx));

  const updateEnrollment = useCallback((idx, field, value) => {
    setEnrollments((prev) => {
      const rows = prev.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        if (field === 'area') {
          updated.category = '';
          updated.intervention = '';
          updated.intervention_type = '';
        }
        if (field === 'category') {
          updated.intervention = '';
          updated.intervention_type = '';
        }
        if (field === 'intervention') {
          updated.intervention_type = resolveInsatstyp(catalog, updated.area, updated.category, value);
        }
        return updated;
      });
      return rows;
    });
  }, [catalog]);

  const toggleMulti = (idx, field, val) => {
    setEnrollments((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const arr = r[field] || [];
        const updated = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
        return { ...r, [field]: updated };
      })
    );
  };

  // ── Sek 3 helpers ─────────────────────────────────────────────────────────
  const setS3Field = (field, value) => {
    setS3((p) => {
      const next = { ...p, [field]: value };
      if (field === 'status') {
        // Rensa villkorade fält vid statusbyte
        next.closure_reason = '';
        next.closure_date = '';
        next.pause_reason = '';
        next.status_comment = '';
      }
      return next;
    });
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  // ── Sek 4: kontaktrader ────────────────────────────────────────────────────
  const addContact = () => setContacts((p) => [...p, emptyContact()]);

  const removeContact = (idx) =>
    setContacts((p) => p.filter((_, i) => i !== idx));

  const updateContact = (idx, field, value) =>
    setContacts((p) => p.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  // ── Sek 7: dokumentladdning ────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_FILETYPES.includes(file.type)) {
      toast.error('Filtyp ej tillåten (tillåtna: PDF, PNG, JPG)');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error('Filen är för stor (max 10 MB)');
      return;
    }
    setDocuments((p) => [
      ...p,
      { file, doc_type: 'gdpr_samtycke', name: file.name, size: file.size, type: file.type },
    ]);
    e.target.value = '';
  };

  const removeDocument = (idx) =>
    setDocuments((p) => p.filter((_, i) => i !== idx));

  const setDocType = (idx, value) =>
    setDocuments((p) => p.map((d, i) => (i === idx ? { ...d, doc_type: value } : d)));

  // ── Validering ─────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};

    if (!s1.first_name.trim()) errs.first_name = 'Förnamn är obligatoriskt';
    if (!s1.last_name.trim()) errs.last_name = 'Efternamn är obligatoriskt';

    if (s1.personal_identity_number && !validatePin(s1.personal_identity_number)) {
      errs.personal_identity_number = 'Ogiltigt personnummerformat (YYYYMMDD-XXXX, YYMMDD-XXXX, 10 eller 12 siffror)';
    }

    if (s1.entity_name && s1.entity_name.length > 120) {
      errs.entity_name = 'Max 120 tecken';
    }

    // Sek 2: valideringsrader
    enrollments.forEach((enr, idx) => {
      if (!enr.area) errs[`enr_${idx}_area`] = 'Område krävs';
      if (!enr.category) errs[`enr_${idx}_category`] = 'Kategori krävs';
      if (!enr.intervention) errs[`enr_${idx}_intervention`] = 'Insats krävs';
      if (!enr.start_date) errs[`enr_${idx}_start_date`] = 'Startdatum krävs';
      if (!enr.planned_end_date) errs[`enr_${idx}_planned_end_date`] = 'Beslutat slutdatum krävs';
    });

    // Sek 3: villkorade
    if (s3.status === 'Avslutad') {
      if (!s3.closure_reason) errs.closure_reason = 'Avslutsorsak är obligatorisk';
      if (!s3.closure_date) errs.closure_date = 'Avslutsdatum är obligatoriskt';
    }
    if (s3.status === 'Pausad' && !s3.pause_reason) {
      errs.pause_reason = 'Pausorsak är obligatorisk';
    }
    if (s3.status === 'Inväntar_beslut' && !s3.status_comment.trim()) {
      errs.status_comment = 'Kommentar krävs vid status Inväntar beslut';
    }

    // Sek 4: rader med data måste vara kompletta
    contacts.forEach((c, idx) => {
      const filled = c.name || c.relationship || c.phone;
      if (filled) {
        if (!c.name.trim()) errs[`contact_${idx}_name`] = 'Namn krävs';
        if (!c.relationship.trim()) errs[`contact_${idx}_relationship`] = 'Relation krävs';
        if (!c.phone.trim()) errs[`contact_${idx}_phone`] = 'Telefon krävs';
      }
    });

    // Sek 5
    if (!s5.gdpr_processing) errs.gdpr_processing = 'Samtycke till databehandling (GDPR) är obligatoriskt';

    return errs;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error('Rätta felen i formuläret innan du sparar');
      return;
    }

    setSub(true);
    try {
      // 1. Skapa deltagaren
      const participantPayload = {
        org_id: user.org_id,
        first_name: s1.first_name.trim(),
        last_name: s1.last_name.trim(),
        personal_identity_number: s1.personal_identity_number.trim() || null,
        email: s1.email.trim() || null,
        phone: s1.phone.trim() || null,
        entity_name: s1.entity_name.trim() || null,
        assigned_user_id: s1.assigned_user_id || null,
        status: s3.status,
        status_comment: s3.status_comment || null,
        closure_reason: s3.status === 'Avslutad' ? s3.closure_reason : null,
        closure_date: s3.status === 'Avslutad' ? s3.closure_date : null,
        pause_reason: s3.status === 'Pausad' ? s3.pause_reason : null,
        consent_given: s5.gdpr_processing,
        gdpr_consent_given: s5.gdpr_processing,
        gdpr_consent_source: 'form',
        important_information: s6.important_information.trim() || null,
      };

      const res = await api.post('/participants', participantPayload);
      const participantId = res.data.id;

      // 2. Verksamhetskopplingar
      for (const enr of enrollments) {
        if (!enr.area) continue;
        await api.post(`/participants/${participantId}/enrollments`, {
          area: enr.area,
          category: enr.category,
          intervention: enr.intervention,
          intervention_type: enr.intervention_type,
          activities: enr.activities,
          outcomes: enr.outcomes,
          start_date: enr.start_date,
          planned_end_date: enr.planned_end_date,
          actual_end_date: enr.actual_end_date || null,
          case_number: enr.case_number || null,
        });
      }

      // 3. Samtycken (detaljerade)
      await api.post(`/participants/${participantId}/consents`, {
        gdpr_processing: s5.gdpr_processing,
        info_sharing: s5.info_sharing,
        contact_support_person: s5.contact_support_person,
      });

      // 4. Anhöriga (rader med data)
      for (const c of contacts) {
        if (c.name.trim() && c.relationship.trim() && c.phone.trim()) {
          await api.post(`/participants/${participantId}/contacts`, {
            name: c.name.trim(),
            relationship: c.relationship.trim(),
            phone: c.phone.trim(),
          });
        }
      }

      // 5. Statushistorik om icke-default status
      if (s3.status !== 'Aktiv') {
        await api.patch(`/participants/${participantId}/status`, {
          status: s3.status,
          status_comment: s3.status_comment || null,
          closure_reason: s3.closure_reason || null,
          closure_date: s3.closure_date || null,
          pause_reason: s3.pause_reason || null,
        }).catch(() => {});
      }

      // 6. Dokument-metadata
      for (const doc of documents) {
        await api.post(`/participants/${participantId}/documents`, {
          document_type: doc.doc_type,
          file_name: doc.name,
          file_type: doc.type,
          file_size: doc.size,
          file_path: '',
        }).catch(() => {});
      }

      toast.success('Deltagare skapad!');
      if (onSave) onSave(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Personnummer redan registrerat i organisationen') {
        setErrors((p) => ({ ...p, personal_identity_number: detail }));
      }
      toast.error(detail || 'Kunde inte skapa deltagare');
    } finally {
      setSub(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6"
      data-testid="create-participant-form"
    >
      {/* ── Sektion 1: Grunduppgifter ──────────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-1">
        <SectionHeader num={1} title="Grunduppgifter" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="first_name">Förnamn <span className="text-destructive">*</span></Label>
            <Input
              id="first_name"
              data-testid="input-first-name"
              value={s1.first_name}
              onChange={(e) => setS1Field('first_name', e.target.value)}
              className={errors.first_name ? 'border-destructive' : ''}
            />
            {errors.first_name && <p role="alert" className="text-xs text-destructive">{errors.first_name}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="last_name">Efternamn <span className="text-destructive">*</span></Label>
            <Input
              id="last_name"
              data-testid="input-last-name"
              value={s1.last_name}
              onChange={(e) => setS1Field('last_name', e.target.value)}
              className={errors.last_name ? 'border-destructive' : ''}
            />
            {errors.last_name && <p role="alert" className="text-xs text-destructive">{errors.last_name}</p>}
          </div>
        </div>

        {/* Personnummer — skyddat fält */}
        <div className="space-y-1.5">
          <Label htmlFor="pin" className="flex items-center gap-1.5">
            Personnummer <span className="text-destructive">*</span>
            <ShieldAlert size={14} className="text-muted-foreground" title="Skyddat fält — visas ej i listvyer" />
          </Label>
          <Input
            id="pin"
            data-testid="input-pin"
            placeholder="YYYYMMDD-XXXX"
            value={s1.personal_identity_number}
            onChange={(e) => setS1Field('personal_identity_number', e.target.value)}
            className={errors.personal_identity_number ? 'border-destructive' : ''}
          />
          {errors.personal_identity_number && (
            <p role="alert" className="text-xs text-destructive">{errors.personal_identity_number}</p>
          )}
          <p className="text-xs text-muted-foreground">Skyddat fält — exponeras inte i listvyer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="phone_s1">Telefon</Label>
            <Input
              id="phone_s1"
              data-testid="input-phone"
              value={s1.phone}
              onChange={(e) => setS1Field('phone', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email_s1">E-post</Label>
            <Input
              id="email_s1"
              type="email"
              data-testid="input-email"
              value={s1.email}
              onChange={(e) => setS1Field('email', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="entity_name">Enhet</Label>
          <Input
            id="entity_name"
            data-testid="input-entity-name"
            placeholder="Max 120 tecken"
            maxLength={120}
            value={s1.entity_name}
            onChange={(e) => setS1Field('entity_name', e.target.value)}
            className={errors.entity_name ? 'border-destructive' : ''}
          />
          {errors.entity_name && <p role="alert" className="text-xs text-destructive">{errors.entity_name}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="assigned_user">Ansvarig handläggare</Label>
          <select
            id="assigned_user"
            data-testid="select-assigned-user"
            value={s1.assigned_user_id}
            onChange={(e) => setS1Field('assigned_user_id', e.target.value)}
            className={selectCls(false)}
          >
            <option value="">Ingen vald</option>
            {orgUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
        </div>
      </Card>

      {/* ── Sektion 2: Verksamhetskoppling ────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-2">
        <div className="flex items-center justify-between">
          <SectionHeader num={2} title="Verksamhetskoppling" sub="En deltagare kan ha flera koppningar." />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addEnrollment}
            data-testid="add-enrollment-btn"
            className="rounded-full text-xs"
          >
            <Plus size={14} className="mr-1" /> Lägg till
          </Button>
        </div>

        {enrollments.length === 0 && (
          <p className="text-sm text-muted-foreground italic" data-testid="no-enrollments-msg">
            Ingen verksamhetskoppling ännu. Klicka "Lägg till" för att lägga till.
          </p>
        )}

        {enrollments.map((enr, idx) => (
          <div
            key={idx}
            data-testid={`enrollment-row-${idx}`}
            className="border rounded-lg p-4 space-y-3 relative bg-muted/20"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold text-muted-foreground">Koppling {idx + 1}</span>
              <button
                type="button"
                onClick={() => removeEnrollment(idx)}
                data-testid={`remove-enrollment-${idx}`}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                aria-label="Ta bort rad"
              >
                <Trash2 size={15} />
              </button>
            </div>

            {/* Cascade: Område → Kategori → Insats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Område <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`enr-area-${idx}`}
                  value={enr.area}
                  onChange={(e) => updateEnrollment(idx, 'area', e.target.value)}
                  className={selectCls(errors[`enr_${idx}_area`])}
                >
                  <option value="">Välj område...</option>
                  {getAreas(catalog).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {errors[`enr_${idx}_area`] && (
                  <p role="alert" className="text-xs text-destructive">{errors[`enr_${idx}_area`]}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label>Kategori <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`enr-category-${idx}`}
                  value={enr.category}
                  onChange={(e) => updateEnrollment(idx, 'category', e.target.value)}
                  disabled={!enr.area}
                  className={selectCls(errors[`enr_${idx}_category`])}
                >
                  <option value="">Välj kategori...</option>
                  {getCategories(catalog, enr.area).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors[`enr_${idx}_category`] && (
                  <p role="alert" className="text-xs text-destructive">{errors[`enr_${idx}_category`]}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label>Insats <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`enr-intervention-${idx}`}
                  value={enr.intervention}
                  onChange={(e) => updateEnrollment(idx, 'intervention', e.target.value)}
                  disabled={!enr.category}
                  className={selectCls(errors[`enr_${idx}_intervention`])}
                >
                  <option value="">Välj insats...</option>
                  {getInterventions(catalog, enr.area, enr.category).map((i) => (
                    <option key={i.insats} value={i.insats}>{i.insats}</option>
                  ))}
                </select>
                {errors[`enr_${idx}_intervention`] && (
                  <p role="alert" className="text-xs text-destructive">{errors[`enr_${idx}_intervention`]}</p>
                )}
              </div>
            </div>

            {/* Insatstyp (autofylls) */}
            {enr.intervention_type && (
              <p className="text-xs text-muted-foreground">
                Insatstyp: <span className="font-semibold" data-testid={`enr-insatstyp-${idx}`}>{enr.intervention_type}</span>
              </p>
            )}

            {/* Aktiviteter (multi-select) */}
            <div className="space-y-1">
              <Label className="text-sm">Aktiviteter</Label>
              <div className="flex flex-wrap gap-2" data-testid={`enr-activities-${idx}`}>
                {ACTIVITIES.map((act) => (
                  <button
                    key={act}
                    type="button"
                    onClick={() => toggleMulti(idx, 'activities', act)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      enr.activities.includes(act)
                        ? 'bg-primary text-primary-foreground border-primary font-bold'
                        : 'border-input text-muted-foreground hover:border-primary'
                    }`}
                  >
                    {act}
                  </button>
                ))}
              </div>
            </div>

            {/* Utfall (multi-select) */}
            <div className="space-y-1">
              <Label className="text-sm">Utfall</Label>
              <div className="flex flex-wrap gap-2" data-testid={`enr-outcomes-${idx}`}>
                {OUTCOMES.map((out) => (
                  <button
                    key={out}
                    type="button"
                    onClick={() => toggleMulti(idx, 'outcomes', out)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      enr.outcomes.includes(out)
                        ? 'bg-primary text-primary-foreground border-primary font-bold'
                        : 'border-input text-muted-foreground hover:border-primary'
                    }`}
                  >
                    {out}
                  </button>
                ))}
              </div>
            </div>

            {/* Datum + ärendenummer */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Startdatum <span className="text-destructive">*</span></Label>
                <Input
                  type="date"
                  data-testid={`enr-start-date-${idx}`}
                  value={enr.start_date}
                  onChange={(e) => updateEnrollment(idx, 'start_date', e.target.value)}
                  className={errors[`enr_${idx}_start_date`] ? 'border-destructive' : ''}
                />
                {errors[`enr_${idx}_start_date`] && (
                  <p role="alert" className="text-xs text-destructive">{errors[`enr_${idx}_start_date`]}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Beslutat slutdatum <span className="text-destructive">*</span></Label>
                <Input
                  type="date"
                  data-testid={`enr-planned-end-${idx}`}
                  value={enr.planned_end_date}
                  onChange={(e) => updateEnrollment(idx, 'planned_end_date', e.target.value)}
                  className={errors[`enr_${idx}_planned_end_date`] ? 'border-destructive' : ''}
                />
                {errors[`enr_${idx}_planned_end_date`] && (
                  <p role="alert" className="text-xs text-destructive">{errors[`enr_${idx}_planned_end_date`]}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Faktiskt avslutsdatum</Label>
                <Input
                  type="date"
                  data-testid={`enr-actual-end-${idx}`}
                  value={enr.actual_end_date}
                  onChange={(e) => updateEnrollment(idx, 'actual_end_date', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Ärendenummer</Label>
              <Input
                data-testid={`enr-case-number-${idx}`}
                value={enr.case_number}
                onChange={(e) => updateEnrollment(idx, 'case_number', e.target.value)}
                placeholder="Valfritt"
              />
            </div>
          </div>
        ))}
      </Card>

      {/* ── Sektion 3: Status ─────────────────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-3">
        <SectionHeader num={3} title="Status" />

        <div className="space-y-1.5">
          <Label htmlFor="status-select">Status <span className="text-destructive">*</span></Label>
          <select
            id="status-select"
            data-testid="select-status"
            value={s3.status}
            onChange={(e) => setS3Field('status', e.target.value)}
            className={selectCls(false)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>

        {/* Statuskommentar — alltid möjlig, krävs vid Inväntar_beslut */}
        <div className="space-y-1.5">
          <Label htmlFor="status-comment">
            Statuskommentar
            {s3.status === 'Inväntar_beslut' && <span className="text-destructive"> *</span>}
          </Label>
          <Textarea
            id="status-comment"
            data-testid="input-status-comment"
            value={s3.status_comment}
            onChange={(e) => setS3Field('status_comment', e.target.value)}
            rows={2}
            className={errors.status_comment ? 'border-destructive' : ''}
          />
          {errors.status_comment && <p role="alert" className="text-xs text-destructive">{errors.status_comment}</p>}
        </div>

        {/* Avslutsorsak + avslutsdatum — villkorat på Avslutad */}
        {s3.status === 'Avslutad' && (
          <div className="space-y-4 pl-4 border-l-2 border-destructive/30" data-testid="closure-fields">
            <div className="space-y-1.5">
              <Label htmlFor="closure-reason">Avslutsorsak <span className="text-destructive">*</span></Label>
              <select
                id="closure-reason"
                data-testid="select-closure-reason"
                value={s3.closure_reason}
                onChange={(e) => setS3Field('closure_reason', e.target.value)}
                className={selectCls(errors.closure_reason)}
              >
                <option value="">Välj orsak...</option>
                {CLOSURE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              {errors.closure_reason && <p role="alert" className="text-xs text-destructive">{errors.closure_reason}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="closure-date">Avslutsdatum <span className="text-destructive">*</span></Label>
              <Input
                id="closure-date"
                type="date"
                data-testid="input-closure-date"
                value={s3.closure_date}
                onChange={(e) => setS3Field('closure_date', e.target.value)}
                className={errors.closure_date ? 'border-destructive' : ''}
              />
              {errors.closure_date && <p role="alert" className="text-xs text-destructive">{errors.closure_date}</p>}
            </div>
          </div>
        )}

        {/* Pausorsak — villkorat på Pausad */}
        {s3.status === 'Pausad' && (
          <div className="space-y-1.5 pl-4 border-l-2 border-yellow-400/50" data-testid="pause-fields">
            <Label htmlFor="pause-reason">Pausorsak <span className="text-destructive">*</span></Label>
            <select
              id="pause-reason"
              data-testid="select-pause-reason"
              value={s3.pause_reason}
              onChange={(e) => setS3Field('pause_reason', e.target.value)}
              className={selectCls(errors.pause_reason)}
            >
              <option value="">Välj orsak...</option>
              {PAUSE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {errors.pause_reason && <p role="alert" className="text-xs text-destructive">{errors.pause_reason}</p>}
          </div>
        )}
      </Card>

      {/* ── Sektion 4: Anhöriga/stödpersoner ──────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-4">
        <div className="flex items-center justify-between">
          <SectionHeader num={4} title="Anhöriga/stödpersoner" sub="Ange kontakter om tillgängliga." />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addContact}
            data-testid="add-contact-btn"
            className="rounded-full text-xs"
          >
            <Plus size={14} className="mr-1" /> Lägg till
          </Button>
        </div>

        {contacts.map((c, idx) => (
          <div
            key={idx}
            data-testid={`contact-row-${idx}`}
            className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end"
          >
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Namn</Label>
              <Input
                data-testid={`contact-name-${idx}`}
                value={c.name}
                onChange={(e) => updateContact(idx, 'name', e.target.value)}
                placeholder="Anna Svensson"
                className={errors[`contact_${idx}_name`] ? 'border-destructive' : ''}
              />
              {errors[`contact_${idx}_name`] && (
                <p role="alert" className="text-xs text-destructive">{errors[`contact_${idx}_name`]}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Relation</Label>
              <Input
                data-testid={`contact-relationship-${idx}`}
                value={c.relationship}
                onChange={(e) => updateContact(idx, 'relationship', e.target.value)}
                placeholder="Make/Maka"
                className={errors[`contact_${idx}_relationship`] ? 'border-destructive' : ''}
              />
              {errors[`contact_${idx}_relationship`] && (
                <p role="alert" className="text-xs text-destructive">{errors[`contact_${idx}_relationship`]}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Telefon</Label>
              <Input
                data-testid={`contact-phone-${idx}`}
                value={c.phone}
                onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                placeholder="070-000 00 00"
                className={errors[`contact_${idx}_phone`] ? 'border-destructive' : ''}
              />
              {errors[`contact_${idx}_phone`] && (
                <p role="alert" className="text-xs text-destructive">{errors[`contact_${idx}_phone`]}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeContact(idx)}
              data-testid={`remove-contact-${idx}`}
              className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
              aria-label="Ta bort kontakt"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </Card>

      {/* ── Sektion 5: Samtycken ───────────────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-5">
        <SectionHeader num={5} title="Samtycken" />

        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="consent-gdpr"
              checked={s5.gdpr_processing}
              onChange={(e) => {
                setS5((p) => ({ ...p, gdpr_processing: e.target.checked }));
                setErrors((p) => ({ ...p, gdpr_processing: '' }));
              }}
              className="mt-0.5 w-4 h-4 rounded border-input"
            />
            <div>
              <span className="font-medium text-sm">
                Deltagaren har informerats om behandling av personuppgifter och gett sitt samtycke enligt GDPR
                <span className="text-destructive"> *</span>
              </span>
              <p className="text-xs text-muted-foreground">Obligatoriskt för att spara deltagaren</p>
            </div>
          </label>
          {errors.gdpr_processing && (
            <p role="alert" className="text-xs text-destructive ml-7">{errors.gdpr_processing}</p>
          )}

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="consent-info-sharing"
              checked={s5.info_sharing}
              onChange={(e) => setS5((p) => ({ ...p, info_sharing: e.target.checked }))}
              className="w-4 h-4 rounded border-input"
            />
            <span className="text-sm">Samtycke till informationsdelning</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="consent-contact-support"
              checked={s5.contact_support_person}
              onChange={(e) => setS5((p) => ({ ...p, contact_support_person: e.target.checked }))}
              className="w-4 h-4 rounded border-input"
            />
            <span className="text-sm">Tillstånd att kontakta anhörig/stödperson</span>
          </label>
        </div>
      </Card>

      {/* ── Sektion 6: Viktig information ─────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-6">
        <SectionHeader
          num={6}
          title="Viktig information"
          sub="Skyddat fält — exponeras inte i listvyer eller rapporter."
        />
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <span>Innehållet i detta fält är skyddat och visas enbart i detaljvyn för behöriga.</span>
        </div>
        <Textarea
          id="important-information"
          data-testid="input-important-info"
          value={s6.important_information}
          onChange={(e) => setS6({ important_information: e.target.value })}
          placeholder="T.ex. medicinsk information, specifika behov, etc."
          rows={3}
        />
      </Card>

      {/* ── Sektion 7: Dokument ───────────────────────────────────────────── */}
      <Card className={sectionCls} data-testid="section-7">
        <SectionHeader num={7} title="Dokument" sub="Max 10 MB per fil. Tillåtna: PDF, PNG, JPG." />

        <label className="flex items-center gap-2 cursor-pointer w-fit border border-dashed border-input rounded-lg px-4 py-3 hover:bg-muted transition-colors">
          <Upload size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Välj fil att ladda upp</span>
          <input
            type="file"
            data-testid="file-input"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>

        {documents.length > 0 && (
          <ul className="space-y-2" data-testid="document-list">
            {documents.map((doc, idx) => (
              <li
                key={idx}
                data-testid={`document-item-${idx}`}
                className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.name}</p>
                  <p className="text-xs text-muted-foreground">{(doc.size / 1024).toFixed(1)} KB</p>
                </div>
                <select
                  value={doc.doc_type}
                  onChange={(e) => setDocType(idx, e.target.value)}
                  data-testid={`doc-type-select-${idx}`}
                  className="h-8 px-2 border rounded text-xs bg-background border-input focus:outline-none"
                >
                  {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => removeDocument(idx)}
                  data-testid={`remove-document-${idx}`}
                  className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                  aria-label="Ta bort dokument"
                >
                  <X size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── Footer: Spara / Avbryt ────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-3 pt-2" data-testid="form-footer">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-full"
        >
          Avbryt
        </Button>
        <Button
          type="submit"
          data-testid="submit-btn"
          disabled={submitting || !s5.gdpr_processing}
          aria-disabled={submitting || !s5.gdpr_processing}
          title={!s5.gdpr_processing ? 'GDPR-samtycke krävs för att spara' : undefined}
          className="rounded-full font-bold px-8"
          style={{ background: '#F9CF3F', color: '#333333' }}
        >
          {submitting ? 'Sparar...' : 'Spara deltagare'}
        </Button>
      </div>
    </form>
  );
};

export default CreateParticipantForm;
